import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import type { DatabaseSearchResult } from '@/models/DatabaseSearch'
import { MAX_SELECTIONS } from '@/utils/databaseSearchSession'
import WebsiteLink from './WebsiteLink'
import {
  BORDER,
  ERROR_TEXT,
  MUTED_TEXT,
  NAVY,
  PANEL,
  primaryButtonSx,
  secondaryButtonSx,
} from './styles'

export const KEEP_NOTE =
  "Kept until you close this browser tab. Changing your filters or searching again doesn't remove them."
// Stands in for the ripple, which these buttons leave off.
const iconFocusSx = {
  '&.Mui-focusVisible': { outline: `2px solid ${NAVY}`, outlineOffset: 2 },
} as const

export const EMPTY_NOTE =
  'Nothing selected. Tick opportunities in the results to add them here.'

interface SelectionsPanelProps {
  open: boolean
  onClose: () => void
  /** In the order ticked. */
  picked: DatabaseSearchResult[]
  onRemove: (row: DatabaseSearchResult) => void
  onClearAll: () => void
  onDownloadCsv: () => void
  onDownloadPdf: () => void
  /** True while the PDF builds; its library loads on first use. */
  pdfPreparing?: boolean
  downloadError?: string | null
  /** Runs once the panel has finished closing. */
  onExited?: () => void
  /** False when the control that opened the panel is gone. */
  restoreFocus?: boolean
}

/**
 * Slides in from the right over a dimmed page; fills the screen on a phone.
 * The close button, Escape and a click on the dimmed page all close it.
 */
export default function SelectionsPanel({
  open,
  onClose,
  picked,
  onRemove,
  onClearAll,
  onDownloadCsv,
  onDownloadPdf,
  pdfPreparing = false,
  downloadError = null,
  onExited,
  restoreFocus = true,
}: SelectionsPanelProps) {
  const [confirmingClear, setConfirmingClear] = useState(false)
  // Held from when the dialog opened: after Clear all the list is empty, and
  // the title would read "Clear all 0 selections?" while the dialog fades.
  const [clearCount, setClearCount] = useState(0)
  const empty = picked.length === 0

  // A removed item takes its × with it. Focus the × now in its place, else
  // the one before it, else the heading.
  const listRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [focusAfterRemove, setFocusAfterRemove] = useState<number | null>(null)
  useEffect(() => {
    if (focusAfterRemove === null) return
    const buttons = listRef.current?.querySelectorAll<HTMLElement>('[data-remove]') ?? []
    const target = buttons[Math.min(focusAfterRemove, buttons.length - 1)] ?? headingRef.current
    target?.focus()
    setFocusAfterRemove(null)
  }, [focusAfterRemove, picked])

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        disableRestoreFocus={!restoreFocus}
        SlideProps={{ onExited }}
        PaperProps={{
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': 'selections-title',
          sx: {
            width: { xs: '100%', sm: 460 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            px: 2.25,
            pt: 2,
            pb: 1,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Box>
            <Typography
              id="selections-title"
              component="h2"
              ref={headingRef}
              tabIndex={-1}
              sx={{ fontSize: 18, fontWeight: 600, color: NAVY, '&:focus': { outline: 'none' } }}
            >
              Your selections
            </Typography>
            <Typography sx={{ fontSize: 14, color: NAVY }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {picked.length} selected
              </Box>{' '}
              <Box component="span" sx={{ color: MUTED_TEXT }}>
                (max {MAX_SELECTIONS})
              </Box>
            </Typography>
          </Box>
          <IconButton
            autoFocus
            disableRipple
            aria-label="Close"
            onClick={onClose}
            sx={{ color: MUTED_TEXT, ...iconFocusSx }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography
          sx={{
            px: 2.25,
            py: 1.25,
            fontSize: 13,
            color: MUTED_TEXT,
            bgcolor: PANEL,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          {KEEP_NOTE}
        </Typography>
        <Box ref={listRef} sx={{ flex: 1, overflow: 'auto', px: 2.25 }}>
          {empty ? (
            <Typography sx={{ py: 2.25, color: MUTED_TEXT }}>{EMPTY_NOTE}</Typography>
          ) : (
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {picked.map((row, index) => (
                <Box
                  component="li"
                  key={row.opportunity_uuid}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.25,
                    py: 1.25,
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }}>{row.funder}</Typography>
                    <Typography sx={{ mt: 0.25, mb: 0.5 }}>{row.opportunity}</Typography>
                    <Typography sx={{ fontSize: 14, color: MUTED_TEXT }}>
                      {row.timing}
                      {row.link && (
                        <>
                          {' · '}
                          <WebsiteLink href={row.link} opportunity={row.opportunity} />
                        </>
                      )}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    disableRipple
                    data-remove
                    aria-label={`Remove ${row.opportunity} from your selections`}
                    onClick={() => {
                      onRemove(row)
                      setFocusAfterRemove(index)
                    }}
                    sx={{
                      alignSelf: 'flex-start',
                      color: MUTED_TEXT,
                      '&:hover': { color: '#b91c1c' },
                      ...iconFocusSx,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.25,
            px: 2.25,
            py: 1.5,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <Button
            variant="outlined"
            disableRipple
            disabled={empty}
            onClick={() => {
              setClearCount(picked.length)
              setConfirmingClear(true)
            }}
            sx={secondaryButtonSx}
          >
            Clear all
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              disableRipple
              disabled={empty}
              onClick={onDownloadCsv}
              sx={primaryButtonSx}
            >
              Download CSV
            </Button>
            <Button
              variant="contained"
              disableRipple
              disabled={empty}
              // aria-disabled while preparing: a disabled button drops focus.
              aria-disabled={pdfPreparing || undefined}
              onClick={() => {
                if (!pdfPreparing) onDownloadPdf()
              }}
              sx={{ ...primaryButtonSx, ...(pdfPreparing && { opacity: 0.7, cursor: 'progress' }) }}
            >
              {pdfPreparing ? 'Preparing…' : 'Download PDF'}
            </Button>
          </Box>
          <Typography
            role="alert"
            sx={{ flexBasis: '100%', fontSize: 14, color: ERROR_TEXT, '&:empty': { display: 'none' } }}
          >
            {downloadError}
          </Typography>
        </Box>
      </Drawer>
      <Dialog
        open={confirmingClear}
        onClose={() => setConfirmingClear(false)}
        aria-labelledby="clear-selections-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="clear-selections-title">
          {clearCount === 1
            ? 'Clear your 1 selection?'
            : `Clear all ${clearCount} selections?`}
        </DialogTitle>
        <DialogContent>
          <Typography>This can't be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disableRipple
            onClick={() => setConfirmingClear(false)}
            sx={{ ...secondaryButtonSx, border: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableRipple
            onClick={() => {
              setConfirmingClear(false)
              onClearAll()
            }}
            sx={primaryButtonSx}
          >
            Clear all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
